import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Flex,
    Heading,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    Button,
    IconButton,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Text,
    VStack,
    HStack,
    SimpleGrid,
    Select,
    Link,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Card,
    CardBody,
    Divider,
    Spinner,
    Textarea,
    List,
    ListItem,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Avatar,
    Tooltip,
    useColorModeValue,
    Tag,
    TagLabel,
    TagLeftIcon,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Portal
} from '@chakra-ui/react';
import {
    ViewIcon,
    DeleteIcon,
    SearchIcon,
    DownloadIcon,
    CheckCircleIcon,
    InfoIcon,
    AttachmentIcon,
    ExternalLinkIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    TimeIcon,
    EditIcon,
    EmailIcon,
    PhoneIcon,
    SmallAddIcon,
    HamburgerIcon,
    CalendarIcon
} from '@chakra-ui/icons';
import { MdAssignment, MdPendingActions, MdFactCheck, MdCancel } from 'react-icons/md';
import axiosInstance from '../services/axiosInstance';
import LayoutDashboard from '../shared/LayoutDashboard';
import { urlvideo } from '../services/urls';

const PAGE_SIZE = 50;

const StatsCard = ({ title, stat, icon, color }) => {
    const bgColor = useColorModeValue('white', 'gray.800');
    return (
        <Card bg={bgColor} borderRadius="2xl" border="1px solid" borderColor="gray.100" boxShadow="sm" overflow="hidden">
            <CardBody p={5}>
                <Flex justify="space-between" align="center">
                    <Box>
                        <Text color="gray.500" fontSize="sm" fontWeight="medium" mb={1}>{title}</Text>
                        <Heading size="lg" fontWeight="bold" color="gray.800">{stat}</Heading>
                    </Box>
                    <Flex 
                        w={12} 
                        h={12} 
                        borderRadius="xl" 
                        bg={`${color}.50`} 
                        color={`${color}.500`} 
                        align="center" 
                        justify="center"
                        fontSize="2xl"
                    >
                        <Icon as={icon} />
                    </Flex>
                </Flex>
            </CardBody>
        </Card>
    );
};

const AdminRecruitmentManagement = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedApp, setSelectedApp] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const primaryColor = '#F2C919';
    const secondaryColor = '#2C3E50';
    const accentBg = 'gray.50';

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, searchTerm]);

    useEffect(() => {
        fetchApplications();
    }, [statusFilter, currentPage]);

    const fetchApplications = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/recruitment', {
                params: {
                    status: statusFilter || undefined,
                    page: currentPage,
                    limit: PAGE_SIZE
                }
            });
            setApplications(response.data.applications);
            if (response.data.pagination) {
                setTotalPages(response.data.pagination.totalPages);
                setTotalItems(response.data.pagination.totalItems);
            }
        } catch (error) {
            toast({
                title: "خطأ",
                description: "تعذر جلب البيانات",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (app) => {
        setSelectedApp(app);
        setAdminNotes(app.adminNotes || '');
        onOpen();
    };

    const handleUpdateStatus = async (newStatus, appId = null) => {
        const id = appId || selectedApp?._id;
        if (!id) return;

        try {
            setIsUpdatingStatus(true);
            const response = await axiosInstance.patch(`/recruitment/${id}/status`, {
                status: newStatus,
                adminNotes: appId ? undefined : adminNotes
            });

            if (response.data.success) {
                toast({
                    title: "تم التحديث",
                    description: "تم تغيير حالة الطلب بنجاح",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                fetchApplications();
                if (!appId) onClose();
            }
        } catch (error) {
            toast({
                title: "خطأ",
                description: "تعذر تحديث الحالة",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("هل أنت متأكد من حذف هذا الطلب؟")) return;

        try {
            await axiosInstance.delete(`/recruitment/${id}`);
            toast({
                title: "تم الحذف",
                description: "تم حذف الطلب بنجاح",
                status: "success",
            });
            fetchApplications();
        } catch (error) {
            toast({
                title: "خطأ",
                description: "تعذر حذف الطلب",
                status: "error",
            });
        }
    };

    const getStatusStyles = (status) => {
        switch (status) {
            case 'pending': return { label: 'قيد الانتظار', color: 'orange', icon: TimeIcon };
            case 'reviewed': return { label: 'تمت المراجعة', color: 'blue', icon: SearchIcon };
            case 'accepted': return { label: 'مقبول', color: 'green', icon: CheckCircleIcon };
            case 'rejected': return { label: 'مرفوض', color: 'red', icon: MdCancel };
            default: return { label: status, color: 'gray', icon: InfoIcon };
        }
    };

    const stats = useMemo(() => {
        return {
            total: totalItems,
            pending: applications.filter(a => a.status === 'pending').length,
            accepted: applications.filter(a => a.status === 'accepted').length,
        };
    }, [applications, totalItems]);

    const filteredApps = applications.filter(app =>
        app.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.phone.includes(searchTerm) ||
        (Array.isArray(app.subjects) ? app.subjects.join(' ').toLowerCase() : (app.subjects || '').toLowerCase()).includes(searchTerm.toLowerCase())
    );

    return (
        <Box p={{ base: 4, md: 8 }} bg={accentBg} minH="100vh" dir="rtl">
            <VStack align="stretch" spacing={8}>
                {/* Header Section */}
                <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'start', md: 'center' }} gap={4}>
                    <Box>
                        <Heading size="xl" color={secondaryColor} fontFamily="Cairo, sans-serif" fontWeight="bold">إدارة الانتدابات</Heading>
                        <Text color="gray.500" mt={1}>إدارة ومراجعة طلبات انضمام الأساتذة الجدد</Text>
                    </Box>
                    <HStack spacing={3}>
                        <InputGroup maxW="300px" bg="white" borderRadius="xl" boxShadow="sm">
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color="gray.400" />
                            </InputLeftElement>
                            <Input
                                placeholder="بحث بالاسم، الهاتف..."
                                border="none"
                                _focus={{ boxShadow: 'none' }}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                        <Select
                            placeholder="كل الحالات"
                            maxW="180px"
                            bg="white"
                            borderRadius="xl"
                            boxShadow="sm"
                            border="none"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="pending">قيد الانتظار</option>
                            <option value="reviewed">تمت المراجعة</option>
                            <option value="accepted">مقبول</option>
                            <option value="rejected">مرفوض</option>
                        </Select>
                    </HStack>
                </Flex>

                {/* Stats Section */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                    <StatsCard title="إجمالي الطلبات" stat={totalItems} icon={MdAssignment} color="blue" />
                    <StatsCard title="جديد (هذه الصفحة)" stat={stats.pending} icon={MdPendingActions} color="orange" />
                    <StatsCard title="مقبول (هذه الصفحة)" stat={stats.accepted} icon={MdFactCheck} color="green" />
                    <Card w="full" bg={secondaryColor} borderRadius="2xl" color="white" boxShadow="xl">
                        <CardBody p={5}>
                            <VStack align="start" spacing={1}>
                                <Text color="whiteAlpha.700" fontSize="sm">آخر تحديث</Text>
                                <Heading size="md" fontWeight="bold">اليوم، 14:30</Heading>
                                <Tag size="sm" mt={2} bg="whiteAlpha.300" color="white" variant="subtle">
                                    <TagLeftIcon as={CalendarIcon} boxSize="10px" />
                                    <TagLabel fontSize="xs">النظام نشط</TagLabel>
                                </Tag>
                            </VStack>
                        </CardBody>
                    </Card>
                </SimpleGrid>

                {/* Main Table Card */}
                <Card bg="white" borderRadius="3xl" boxShadow="2xl" overflow="hidden" border="1px solid" borderColor="gray.100">
                    <CardBody p={0}>
                        <Box overflowX="auto">
                            {loading ? (
                                <Flex p={20} justify="center" direction="column" align="center" gap={4}>
                                    <Spinner size="xl" thickness="4px" speed="0.65s" color={primaryColor} />
                                    <Text color="gray.500" fontWeight="medium">جاري تحميل البيانات...</Text>
                                </Flex>
                            ) : (
                                <>
                                    <Table variant="simple" size="lg">
                                        <Thead bg="gray.50">
                                            <Tr>
                                                <Th textAlign="right" color="gray.600" fontSize="xs" textTransform="uppercase" py={5}>الأستاذ</Th>
                                                <Th textAlign="right" color="gray.600" fontSize="xs" textTransform="uppercase" py={5}>بيانات الاتصال</Th>
                                                <Th textAlign="right" color="gray.600" fontSize="xs" textTransform="uppercase" py={5}>الموقع والمادة</Th>
                                                <Th textAlign="right" color="gray.600" fontSize="xs" textTransform="uppercase" py={5}>الحالة</Th>
                                                <Th textAlign="center" color="gray.600" fontSize="xs" textTransform="uppercase" py={5}>الإجراءات</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {filteredApps.length === 0 ? (
                                                <Tr>
                                                    <Td colSpan={5} textAlign="center" py={20}>
                                                        <VStack spacing={4}>
                                                            <Box bg="gray.50" p={6} borderRadius="full">
                                                                <Icon as={MdAssignment} boxSize={12} color="gray.300" />
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xl" fontWeight="bold" color="gray.700">لا توجد طلبات</Text>
                                                                <Text color="gray.500">جرب تغيير الفلتر أو كلمة البحث</Text>
                                                            </Box>
                                                        </VStack>
                                                    </Td>
                                                </Tr>
                                            ) : (
                                                filteredApps.map((app) => {
                                                    const s = getStatusStyles(app.status);
                                                    return (
                                                        <Tr key={app._id} _hover={{ bg: 'blue.50', transition: '0.2s' }}>
                                                            <Td>
                                                                <HStack spacing={3}>
                                                                    <Avatar size="sm" name={app.fullName} bg={secondaryColor} color="white" />
                                                                    <VStack align="start" spacing={0}>
                                                                        <Text fontWeight="bold" color="gray.800">{app.fullName}</Text>
                                                                        <Text fontSize="xs" color="gray.500">تم تقديمه منذ {new Date(app.createdAt).toLocaleDateString('ar-TN')}</Text>
                                                                    </VStack>
                                                                </HStack>
                                                            </Td>
                                                            <Td>
                                                                <VStack align="start" spacing={1}>
                                                                    <HStack color="gray.600" spacing={2}>
                                                                        <Icon as={PhoneIcon} boxSize={3} />
                                                                        <Text fontSize="sm">{app.phone}</Text>
                                                                    </HStack>
                                                                    {app.email && (
                                                                        <HStack color="gray.600" spacing={2}>
                                                                            <Icon as={EmailIcon} boxSize={3} />
                                                                            <Text fontSize="xs">{app.email}</Text>
                                                                        </HStack>
                                                                    )}
                                                                </VStack>
                                                            </Td>
                                                            <Td>
                                                                <VStack align="start" spacing={1}>
                                                                    <Tag size="sm" variant="subtle" colorScheme="blue" borderRadius="full">
                                                                        {app.state}
                                                                    </Tag>
                                                                    <Text fontSize="sm" color="gray.700" noOfLines={1} maxW="150px">
                                                                        {Array.isArray(app.subjects) ? app.subjects.join(', ') : app.subjects}
                                                                    </Text>
                                                                </VStack>
                                                            </Td>
                                                            <Td>
                                                                <Badge 
                                                                    px={3} 
                                                                    py={1} 
                                                                    borderRadius="full" 
                                                                    variant="subtle" 
                                                                    colorScheme={s.color}
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    w="fit-content"
                                                                    gap={1}
                                                                >
                                                                    <Icon as={s.icon} boxSize={3} />
                                                                    {s.label}
                                                                </Badge>
                                                            </Td>
                                                            <Td textAlign="center">
                                                                <HStack spacing={1} justify="center">
                                                                    <Tooltip label="عرض وتعديل" borderRadius="lg">
                                                                        <IconButton
                                                                            icon={<ViewIcon />}
                                                                            colorScheme="blue"
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            borderRadius="full"
                                                                            onClick={() => handleViewDetails(app)}
                                                                            aria-label="عرض"
                                                                        />
                                                                    </Tooltip>
                                                                    
                                                                    <Menu isLazy p={0}>
                                                                        <MenuButton
                                                                            as={IconButton}
                                                                            icon={<HamburgerIcon />}
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            borderRadius="full"
                                                                            aria-label="خيارات إضافية"
                                                                        />
                                                                        <Portal>
                                                                            <MenuList borderRadius="xl" shadow="xl" border="1px solid" borderColor="gray.100" bg="white" zIndex={2000}>
                                                                                <MenuItem icon={<CheckCircleIcon color="green.500" fontSize="14px" />} onClick={() => handleUpdateStatus('accepted', app._id)}>قبول سريع</MenuItem>
                                                                                <MenuItem icon={<InfoIcon color="blue.500" fontSize="14px" />} onClick={() => handleUpdateStatus('reviewed', app._id)}>مراجعة سريعة</MenuItem>
                                                                                <MenuItem icon={<EditIcon color="orange.500" fontSize="14px" />} onClick={() => handleViewDetails(app)}>إضافة ملاحظات</MenuItem>
                                                                                <Divider my={2} />
                                                                                <MenuItem icon={<DeleteIcon color="red.500" fontSize="14px" />} onClick={() => handleDelete(app._id)} color="red.500">حذف الطلب</MenuItem>
                                                                            </MenuList>
                                                                        </Portal>
                                                                    </Menu>
                                                                </HStack>
                                                            </Td>
                                                        </Tr>
                                                    );
                                                })
                                            )}
                                        </Tbody>
                                    </Table>

                                    {/* Pagination controls */}
                                    {totalPages > 1 && (
                                        <Flex
                                            justify="center"
                                            align="center"
                                            py={8}
                                            px={6}
                                            bg="gray.50"
                                            gap={2}
                                        >
                                            <IconButton
                                                icon={<ChevronRightIcon />}
                                                size="md"
                                                variant="white"
                                                bg="white"
                                                boxShadow="sm"
                                                borderRadius="xl"
                                                aria-label="الصفحة السابقة"
                                                isDisabled={currentPage === 1}
                                                onClick={() => setCurrentPage(p => p - 1)}
                                            />
                                            <HStack spacing={2} mx={4}>
                                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                                    .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                                                    .reduce((acc, p, idx, arr) => {
                                                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                                                        acc.push(p);
                                                        return acc;
                                                    }, [])
                                                    .map((item, idx) =>
                                                        item === '...' ? (
                                                            <Text key={`ellipsis-${idx}`} px={2} color="gray.400">…</Text>
                                                        ) : (
                                                            <Button
                                                                key={item}
                                                                size="md"
                                                                variant={item === currentPage ? 'solid' : 'ghost'}
                                                                borderRadius="xl"
                                                                bg={item === currentPage ? primaryColor : 'transparent'}
                                                                color={item === currentPage ? secondaryColor : 'gray.600'}
                                                                fontWeight="bold"
                                                                onClick={() => setCurrentPage(item)}
                                                                minW="45px"
                                                                _hover={item === currentPage ? {} : { bg: 'white', shadow: 'sm' }}
                                                            >
                                                                {item}
                                                            </Button>
                                                        )
                                                    )
                                                }
                                            </HStack>
                                            <IconButton
                                                icon={<ChevronLeftIcon />}
                                                size="md"
                                                variant="white"
                                                bg="white"
                                                boxShadow="sm"
                                                borderRadius="xl"
                                                aria-label="الصفحة التالية"
                                                isDisabled={currentPage === totalPages}
                                                onClick={() => setCurrentPage(p => p + 1)}
                                            />
                                        </Flex>
                                    )}
                                </>
                            )}
                        </Box>
                    </CardBody>
                </Card>
            </VStack>

            {/* Details Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
                <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.300" />
                <ModalContent borderRadius="3xl" overflow="hidden" dir="rtl" boxShadow="2xl">
                    <ModalHeader bg="gray.50" borderBottom="1px solid" borderColor="gray.100" py={6}>
                        <HStack spacing={4}>
                            <Avatar size="md" name={selectedApp?.fullName} bg={secondaryColor} />
                            <VStack align="start" spacing={0}>
                                <Text fontSize="xl" fontWeight="bold">تفاصيل طلب الانتداب</Text>
                                <Text fontSize="sm" color="gray.500">مراجعة الملف الشخصي والمهني للمتقدم</Text>
                            </VStack>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton left="14px" top="18px" borderRadius="full" boxSize="10" />
                    <ModalBody py={8} px={8}>
                        {selectedApp && (
                            <VStack align="stretch" spacing={10}>
                                <Box>
                                    <Heading size="sm" color={primaryColor} mb={6} textTransform="uppercase" letterSpacing="widest">المعلومات الشخصية</Heading>
                                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                                        <VStack align="start" spacing={1}>
                                            <Text fontWeight="bold" color="gray.400" fontSize="xs" textTransform="uppercase">الاسم واللقب</Text>
                                            <Text fontSize="lg" fontWeight="semibold">{selectedApp.fullName}</Text>
                                        </VStack>
                                        <VStack align="start" spacing={1}>
                                            <Text fontWeight="bold" color="gray.400" fontSize="xs" textTransform="uppercase">رقم الهاتف</Text>
                                            <Text fontSize="lg" fontWeight="semibold">{selectedApp.phone}</Text>
                                        </VStack>
                                        <VStack align="start" spacing={1}>
                                            <Text fontWeight="bold" color="gray.400" fontSize="xs" textTransform="uppercase">الولاية</Text>
                                            <Tag colorScheme="blue" borderRadius="full" fontWeight="bold">{selectedApp.state}</Tag>
                                        </VStack>
                                        <VStack align="start" spacing={1}>
                                            <Text fontWeight="bold" color="gray.400" fontSize="xs" textTransform="uppercase">المستوى التعليمي</Text>
                                            <Text fontSize="md" fontWeight="medium">{selectedApp.educationLevel}</Text>
                                        </VStack>
                                    </SimpleGrid>
                                </Box>

                                <Divider />

                                <Box>
                                    <Heading size="sm" color={primaryColor} mb={6} textTransform="uppercase" letterSpacing="widest">التخصص والخبرة</Heading>
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                                        <VStack align="start" spacing={3}>
                                            <Text fontWeight="bold" color="gray.400" fontSize="xs" textTransform="uppercase">المواد المدرسّة</Text>
                                            <HStack wrap="wrap" spacing={2}>
                                                {(Array.isArray(selectedApp.subjects) ? selectedApp.subjects : [selectedApp.subjects]).map((sub, i) => (
                                                    <Tag key={i} colorScheme="purple" variant="solid" borderRadius="lg" px={3}>{sub}</Tag>
                                                ))}
                                            </HStack>
                                        </VStack>
                                        <VStack align="start" spacing={3}>
                                            <Text fontWeight="bold" color="gray.400" fontSize="xs" textTransform="uppercase">المستويات المدرسّة</Text>
                                            <HStack wrap="wrap" spacing={2}>
                                                {(Array.isArray(selectedApp.levels) ? selectedApp.levels : [selectedApp.levels]).map((lvl, i) => (
                                                    <Tag key={i} colorScheme="cyan" variant="solid" borderRadius="lg" px={3}>{lvl}</Tag>
                                                ))}
                                            </HStack>
                                        </VStack>
                                    </SimpleGrid>
                                    
                                    {selectedApp.sections && selectedApp.sections.length > 0 && (
                                        <Box mt={8}>
                                            <Text fontWeight="bold" color="gray.400" fontSize="xs" textTransform="uppercase" mb={4}>الشعب حسب المستوى</Text>
                                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                                {selectedApp.sections.map((item, idx) => (
                                                    <Card key={idx} variant="outline" borderRadius="xl" borderStyle="dashed">
                                                        <CardBody p={3}>
                                                            <HStack justify="space-between">
                                                                <Badge colorScheme="gray" variant="solid" borderRadius="md">
                                                                    {item.level === 'BAC' ? 'باكالوريا' : item.level === '2' ? 'الثانية ثانوي' : item.level === '3' ? 'الثالثة ثانوي' : item.level}
                                                                </Badge>
                                                                <HStack wrap="wrap" justify="end">
                                                                    {(item.sections || []).map((sec, i) => (
                                                                        <Tag key={i} size="sm" colorScheme="orange" variant="outline">{sec}</Tag>
                                                                    ))}
                                                                </HStack>
                                                            </HStack>
                                                        </CardBody>
                                                    </Card>
                                                ))}
                                            </SimpleGrid>
                                        </Box>
                                    )}

                                    <Box mt={8}>
                                        <Text fontWeight="bold" color="gray.400" fontSize="xs" textTransform="uppercase" mb={3}>المؤسسات التعليمية</Text>
                                        <HStack wrap="wrap" spacing={2}>
                                            {(Array.isArray(selectedApp.institution) ? selectedApp.institution : [selectedApp.institution]).map((inst, i) => (
                                                <Tag key={i} variant="subtle" colorScheme="gray" py={1} px={3} borderRadius="full">• {inst}</Tag>
                                            ))}
                                        </HStack>
                                    </Box>

                                    <Box mt={8}>
                                        <Text fontWeight="bold" color="gray.400" fontSize="xs" textTransform="uppercase" mb={3}>الخبرة المهنية</Text>
                                        <Box bg="gray.50" p={5} borderRadius="2xl" border="1px solid" borderColor="gray.100">
                                            <Text fontSize="md" lineHeight="tall">{selectedApp.experience}</Text>
                                        </Box>
                                    </Box>
                                </Box>

                                <Divider />

                                <Box>
                                    <Heading size="sm" color={primaryColor} mb={6} textTransform="uppercase" letterSpacing="widest">الأدوات والوثائق</Heading>
                                    <Flex gap={10} mb={8}>
                                        <Tag size="lg" variant="subtle" colorScheme={selectedApp.hasTablet ? "green" : "gray"} borderRadius="full" p={3}>
                                            <TagLeftIcon as={selectedApp.hasTablet ? CheckCircleIcon : InfoIcon} />
                                            <TagLabel>لوحة رقمية (Tablet)</TagLabel>
                                        </Tag>
                                        <Tag size="lg" variant="subtle" colorScheme={selectedApp.hasPDF ? "green" : "gray"} borderRadius="full" p={3}>
                                            <TagLeftIcon as={selectedApp.hasPDF ? CheckCircleIcon : InfoIcon} />
                                            <TagLabel>دروس PDF جاهزة</TagLabel>
                                        </Tag>
                                    </Flex>

                                    <Text fontWeight="bold" color="gray.400" fontSize="xs" textTransform="uppercase" mb={4}>الخبرات والشهادات المرفقة</Text>
                                    {selectedApp.educationDocuments && selectedApp.educationDocuments.length > 0 ? (
                                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                            {selectedApp.educationDocuments.map((doc, idx) => (
                                                <Card key={idx} variant="outline" borderRadius="2xl" _hover={{ shadow: 'md', borderColor: primaryColor }} transition="0.3s">
                                                    <CardBody p={4}>
                                                        <Flex align="center" gap={4}>
                                                            <Box bg={`${primaryColor}20`} p={3} borderRadius="xl">
                                                                <AttachmentIcon color={primaryColor} />
                                                            </Box>
                                                            <VStack align="start" spacing={0} flex={1}>
                                                                <Text fontSize="sm" fontWeight="bold" isTruncated maxW="180px">{doc.originalFilename}</Text>
                                                                <Text fontSize="xs" color="gray.400">{(doc.size / 1024 / 1024).toFixed(2)} MB</Text>
                                                            </VStack>
                                                            <HStack>
                                                                <Tooltip label="تحميل">
                                                                    <IconButton
                                                                        as="a"
                                                                        href={`${urlvideo.endsWith('/') ? urlvideo.slice(0, -1) : urlvideo}/api/recruitment/download/${selectedApp._id}/${idx}`}
                                                                        target="_blank"
                                                                        icon={<DownloadIcon />}
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        borderRadius="full"
                                                                        aria-label="تحميل"
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip label="فتح في نافذة جديدة">
                                                                    <IconButton
                                                                        as="a"
                                                                        href={`${urlvideo.endsWith('/') ? urlvideo.slice(0, -1) : urlvideo}/${doc.path.startsWith('/') ? doc.path.slice(1) : doc.path}`}
                                                                        target="_blank"
                                                                        icon={<ExternalLinkIcon />}
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        borderRadius="full"
                                                                        aria-label="عرض"
                                                                    />
                                                                </Tooltip>
                                                            </HStack>
                                                        </Flex>
                                                    </CardBody>
                                                </Card>
                                            ))}
                                        </SimpleGrid>
                                    ) : (
                                        <Text color="gray.400" fontSize="sm">لا توجد وثائق مرفقة</Text>
                                    )}
                                </Box>

                                <Divider />

                                <Box pb={4}>
                                    <Heading size="sm" color={primaryColor} mb={6} textTransform="uppercase" letterSpacing="widest">ملاحظات الإدارة لاتخاذ القرار</Heading>
                                    <Textarea
                                        value={adminNotes}
                                        onChange={(e) => setAdminNotes(e.target.value)}
                                        placeholder="اكتب ملاحظاتك التقييمية عن هذا الأستاذ هنا..."
                                        rows={4}
                                        borderRadius="2xl"
                                        bg="white"
                                        shadow="inner"
                                        _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                    />
                                </Box>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter bg="gray.50" py={6} px={8} borderTop="1px solid" borderColor="gray.100">
                        <HStack spacing={4} w="100%" justify="center">
                            <Button 
                                size="lg" 
                                leftIcon={<CheckCircleIcon />} 
                                colorScheme="green" 
                                px={10} 
                                borderRadius="2xl" 
                                onClick={() => handleUpdateStatus('accepted')} 
                                isLoading={isUpdatingStatus}
                                shadow="md"
                            >
                                قبول الأستاذ
                            </Button>
                            <Button 
                                size="lg" 
                                leftIcon={<SearchIcon />} 
                                colorScheme="blue" 
                                px={8} 
                                borderRadius="2xl" 
                                onClick={() => handleUpdateStatus('reviewed')} 
                                isLoading={isUpdatingStatus}
                                shadow="sm"
                            >
                                تم التقييم
                            </Button>
                            <Button 
                                size="lg" 
                                leftIcon={<MdCancel />} 
                                colorScheme="red" 
                                variant="outline" 
                                px={8} 
                                borderRadius="2xl" 
                                onClick={() => handleUpdateStatus('rejected')} 
                                isLoading={isUpdatingStatus}
                            >
                                رفض الطلب
                            </Button>
                            <Button size="lg" variant="ghost" px={8} borderRadius="2xl" onClick={onClose}>إغلاق</Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default AdminRecruitmentManagement;
